import axios from 'axios';

// Create a configured instance
const apiClient = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Interceptor to attach tokens automatically
apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('accessToken');
  const loginTimestamp = localStorage.getItem('loginTimestamp');

  // Check if session has been active for more than 24 hours (86400000 ms)
  if (loginTimestamp) {
    const timeElapsed = Date.now() - parseInt(loginTimestamp, 10);
    const TWENTY_FOUR_HOURS = 24 * 60 * 60 * 1000;

    if (timeElapsed > TWENTY_FOUR_HOURS) {
      console.warn('Session exceeded 24 hours. Auto logging out...');
      handleSessionExpiry('Security alert: Your session has exceeded the 24 hour limit and has been securely terminated. Please login again.');
      // Reject to prevent the API call
      return Promise.reject(new axios.Cancel('Session exceeded 24 hours limit.'));
    }
  }

  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export const handleSessionExpiry = (customMessage = 'Session expired') => {
  alert(customMessage);
  // Clear auth data
  localStorage.removeItem('accessToken');
  localStorage.removeItem('refreshToken');
  localStorage.removeItem('user');
  localStorage.removeItem('loginTimestamp');
  localStorage.removeItem('dashboard');

  // Redirect to signin
  if (window.location.pathname !== '/signin') {
    window.location.href = '/signin';
  }
};

// Interceptor to handle expired tokens and redirect to login
apiClient.interceptors.response.use(
  (response) => {
    // Some APIs might return HTTP 200 but with 'MT' or similar string indicating session expiry
    if (response.data === 'MT' || response.data === 'Invalid token' || response.data === 'Token expired') {
      console.warn('Authentication token expired or invalid (from 200 response). Redirecting to login...');
      handleSessionExpiry();
      return Promise.reject(new Error('Session expired'));
    }
    return response;
  },
  (error) => {
    const { response } = error;

    // Check if it's a 401 Unauthorized or has the specific "token_not_valid" code
    // The user provided structure: { code: "token_not_valid", messages: [{ message: "Token is expired" }] }
    const isUnauthorized = response && response.status === 401;
    const isTokenExpired = response &&
      response.data &&
      (response.data.code === 'token_not_valid' ||
        response.data === 'MT' ||
        (response.data.messages && response.data.messages.some(m => m.message === 'Token is expired')));

    // Only fire session expiry if it's explicitly a token expiry, 
    // OR if we get a 401 AND we are not on an auth page (where 401 usually means wrong password/OTP)
    const isAuthPage = ['/signin', '/signup', '/forgot-password'].includes(window.location.pathname);
    
    if (isTokenExpired || (isUnauthorized && !isAuthPage)) {
      console.warn('Authentication token expired or invalid. Redirecting to login...');
      handleSessionExpiry();
    }

    return Promise.reject(error);
  }
);

export default apiClient;